import React, { FC } from "react";
import { notification, Table } from "antd";
import {
  GetReportSchedulesWithExtendedReport,
  useDeleteReportSchedulesMutation,
  useExecuteReportMutation,
} from "shared/api";
import { useGetEmailsColumns } from "shared/hooks/useGetRecipientListColumns";
import { dayjs, handleRequestError } from "shared/helpers";
import { useConfirmDeleteModal } from "shared/hooks";
import { useTranslation } from "react-i18next";

interface RecipientTableProps {
  dataSource?: GetReportSchedulesWithExtendedReport[] | undefined;
  showEditEmailModal: (id: number) => void;
  reportTypeOptions:
    | {
        label: string;
        value: string;
      }[]
    | undefined;
  filters: {
    report: {
      value: string[];
      onChange: (value: string[]) => void;
      options?: { label: string; value: string }[];
    };
    recipient: {
      value: string;
      onChange: (e: string | React.ChangeEvent<HTMLInputElement>) => void;
      clear: () => void;
    };
    periodicity: {
      value: string;
      onChange: (value: string) => void;
    };
  };
}

const RecipientTable: FC<RecipientTableProps> = ({
  dataSource = [],
  showEditEmailModal,
  reportTypeOptions,
  filters,
}) => {
  const { t } = useTranslation();
  const [deleteSchedule] = useDeleteReportSchedulesMutation();
  const [executeReport] = useExecuteReportMutation();

  const runTestReport = async (reportId: number) => {
    try {
      const today = `${dayjs().format("YYYY-MM-DD")} 06:00:00`;
      const yesterday = `${dayjs()
        .subtract(1, "day")
        .format("YYYY-MM-DD")} 06:00:00`;

      const response = await executeReport({
        id: reportId,
        report_format: "pdf",
        order_production_datetime_start: yesterday,
        order_production_datetime_end: today,
        order_id: null,
        material_id: null,
        material_item_id: null,
        packaging_id: null,
        packaging_ids: null,
        quality_result_id: null,
        drigging_rigg_datetime_start: null,
        drigging_rigg_datetime_end: null,
        drigging_rigg_field: null,
        drigging_rigg_n_points: null,
        company_id: null,
        material_category_id_list: [],
        order_ids: [],
        dynamic_tracker_fields: [],
        material_category_id: null,
        product_card_id: null,
        quality_result_material_item_timestamp_end: null,
        quality_result_material_item_timestamp_start: null,
      }).unwrap();

      const url = URL.createObjectURL(
        new Blob([response], { type: "application/pdf" })
      );
      window.open(url, "_blank");
    } catch (error) {
      notification.error({
        message: "Error generating test report",
      });
    }
  };

  const [api, contextHolder] = notification.useNotification();

  const handleDelete = async (id: number) => {
    try {
      await deleteSchedule({ id }).unwrap();
      api.success({
        message: "Success",
        description: "Schedule has been deleted successfully.",
      });
    } catch (error) {
      const description = handleRequestError(
        error,
        "Error while deleting schedule"
      );
      api.error({
        message: "Error",
        description,
      });
    }
  };

  const showDeleteUserModal = useConfirmDeleteModal(
    t("modal-placeholders.delete-warning-message"),
    handleDelete
  );

  const columns = useGetEmailsColumns({
    showDeleteUserModal,
    showEditEmailModal,
    reportTypeOptions,
    filters,
    runTestReport,
  });

  return (
    <>
      {contextHolder}
      <Table
        columns={columns}
        dataSource={dataSource}
        rowKey="id"
        pagination={false}
      />
    </>
  );
};

export { RecipientTable };
